/* Teams within one class: create a team, join an existing team, and (once a
 * member) manage that team's games via TeamPanel. */
import { useState } from 'react';
import { useAuth, useTeams, useCreateTeam, useJoinTeam } from '../../hooks/queries';
import { Button, Card, Input, EmptyNote } from '../../components/ui';
import { TeamPanel } from './TeamPanel';

export function ClassTeams({ classId, name, simLength }: { classId: string; name: string; simLength: number }) {
  const { user } = useAuth();
  const teams = useTeams(classId);
  const createTeam = useCreateTeam();
  const joinTeam = useJoinTeam();
  const [teamName, setTeamName] = useState('');

  const isMember = (members?: { userId: string }[]) => !!members?.some((m) => m.userId === user?.id);

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="text-lg font-bold">{name}</div>
          <div className="text-xs text-mut">{simLength} quarters</div>
        </div>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => { e.preventDefault(); createTeam.mutate({ classId, name: teamName, solo: false }, { onSuccess: () => setTeamName('') }); }}
        >
          <Input value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="New team name" className="w-44" required />
          <Button type="submit" variant="ghost" disabled={createTeam.isPending}>Create team</Button>
        </form>
      </div>

      {teams.isLoading ? (
        <div className="text-sm text-mut">Loading teams…</div>
      ) : (teams.data?.length ?? 0) === 0 ? (
        <EmptyNote>No teams yet. Create one above to start a company.</EmptyNote>
      ) : (
        <div className="space-y-3">
          {teams.data!.map((t) => (
            <div key={t.id} className="rounded-sm border border-card-b bg-bg2/40 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <span className="font-semibold">{t.name}</span>
                  <span className="ml-2 text-xs text-mut">
                    {(t.members?.length ?? 0)} member{(t.members?.length ?? 0) === 1 ? '' : 's'}
                    {t.members?.length ? ` · ${t.members.map((m) => m.user?.name).filter(Boolean).join(', ')}` : ''}
                  </span>
                </div>
                {!isMember(t.members) && (
                  <Button variant="ghost" className="px-3 py-1.5" onClick={() => joinTeam.mutate(t.id)}>
                    Join team
                  </Button>
                )}
              </div>
              {isMember(t.members) && <TeamPanel teamId={t.id} />}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
